import type { RouteRecordRaw } from 'vue-router';
import {
  features,
  useAccessControlStore,
  useFeatureManagerStore,
  useUserContextStore,
} from '@peanuds/app-shell';
import { useUserAccountsStore } from '@peanuds/shared-state';
import { isBatchCollectionAllowedAccount } from './batch-transactions/internal/batch-collection-accounts';

export const BATCH_TRANSACTIONS_ROUTE_NAME = 'batch-transactions';
export const BATCH_TRANSACTION_DETAILS_ROUTE_NAME = 'batch-transaction-details';
export const BATCH_COLLECTION_DRAFT_REVIEW_ROUTE_NAME = 'batch-collection-draft-review';

export const batchCollectionsAccessGuard = async () => {
  const featureManagerStore = useFeatureManagerStore();
  const accessControlStore = useAccessControlStore();

  if (!featureManagerStore.isEnabled(features.batchCollections)) {
    return { name: 'send-money' };
  }

  if (!accessControlStore.isAccessResolved) {
    await accessControlStore.ensureAccessResolved();
  }
  if (!accessControlStore.isAccessResolved) return true;

  if (!accessControlStore.isOnboarded) {
    return { name: 'send-money' };
  }

  await Promise.allSettled([
    useUserAccountsStore().refreshAccounts(),
    useUserContextStore().loadUserContexts({ force: true }),
  ]);

  return true;
};

export const batchCollectionDetailsAccessGuard = async () => {
  try {
    const accessResult = await batchCollectionsAccessGuard();
    if (accessResult !== true) return accessResult;
    if (!useAccessControlStore().isAccessResolved) return true;

    const accountStore = useUserAccountsStore();
    if (!accountStore.hasLoadedAccounts || accountStore.error !== undefined) return true;
    const userContextStore = useUserContextStore();
    if (!userContextStore.isLoaded) return true;
    if (!accountStore.accounts.some(isBatchCollectionAllowedAccount)) {
      return { name: BATCH_TRANSACTIONS_ROUTE_NAME };
    }

    return userContextStore.selectedOrganization?.sddCreditorIdStatus === 'APPROVED'
      ? true
      : { name: BATCH_TRANSACTIONS_ROUTE_NAME };
  } catch {
    // Readiness failures are rendered by the page with a retry action.
    return true;
  }
};

export const batchCollectionsRoutes: RouteRecordRaw[] = [
  {
    path: 'send-money/batch-transactions',
    name: BATCH_TRANSACTIONS_ROUTE_NAME,
    component: () => import('./batch-transactions/BatchCollectionsPage.vue'),
    beforeEnter: batchCollectionsAccessGuard,
    props: true,
    meta: { title: 'Direct Debit Collections' },
  },
  {
    path: 'send-money/batch-transactions/:batchId',
    name: BATCH_TRANSACTION_DETAILS_ROUTE_NAME,
    component: () => import('./batch-transactions/BatchCollectionDetailsPage.vue'),
    beforeEnter: batchCollectionDetailsAccessGuard,
    props: true,
    meta: { title: 'Batch Collection Details' },
  },
  {
    path: 'send-money/batch-transactions/:draftId/review',
    name: BATCH_COLLECTION_DRAFT_REVIEW_ROUTE_NAME,
    component: () => import('./batch-transactions/BatchCollectionsPage.vue'),
    beforeEnter: batchCollectionDetailsAccessGuard,
    props: true,
    meta: { title: 'Review Batch Collection' },
  },
];
